import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
    ArrowLeft, Award, CheckCircle2, Circle, XCircle, TrendingUp, TrendingDown,
    Minus, FileDown, Loader2, GraduationCap, BarChart3,
} from "lucide-react";
import api from "../lib/api";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

const LEVEL_META = {
    acquis: { label: "Acquis", color: "text-emerald-700", bg: "bg-emerald-50", border: "border-emerald-200", chip: "#15A36A", icon: CheckCircle2 },
    partiel: { label: "En cours", color: "text-amber-700", bg: "bg-amber-50", border: "border-amber-200", chip: "#F59E0B", icon: Circle },
    non_acquis: { label: "Non acquis", color: "text-red-700", bg: "bg-red-50", border: "border-red-200", chip: "#DC2626", icon: XCircle },
    non_evalue: { label: "Non évalué", color: "text-muted2", bg: "bg-rule", border: "border-rule", chip: "#94A3B8", icon: Circle },
};

const EVOLUTION_META = {
    progression: { label: "En progression", icon: TrendingUp, color: "text-emerald-700" },
    regression: { label: "En baisse", icon: TrendingDown, color: "text-red-700" },
    stable: { label: "Stable", icon: Minus, color: "text-muted2" },
};

function BarChart({ domains }) {
    // Simple horizontal stacked bar showing acquis/partiel/non_acquis distribution
    return (
        <div className="space-y-4" data-testid="bilan-global-chart">
            {domains.map((d) => {
                const total = d.total || 1;
                const pctA = (d.acquis / total) * 100;
                const pctP = (d.partiel / total) * 100;
                const pctN = (d.non_acquis / total) * 100;
                const pctNE = ((d.non_evalue || 0) / total) * 100;
                return (
                    <div key={d.category} data-testid={`chart-domain-${d.category}`}>
                        <div className="flex items-baseline justify-between mb-2">
                            <div className="font-semibold text-sm">{d.label}</div>
                            <div className="text-[11px] font-mono text-muted2">
                                {d.acquis + d.partiel} / {d.total} maîtrisées
                            </div>
                        </div>
                        <div className="flex h-6 overflow-hidden border border-rule">
                            {pctA > 0 && <div className="bg-emerald-500 flex items-center justify-center text-white text-[10px] font-mono" style={{ width: `${pctA}%` }} title={`${d.acquis} acquis`}>{d.acquis > 0 && `${d.acquis}`}</div>}
                            {pctP > 0 && <div className="bg-amber-500 flex items-center justify-center text-white text-[10px] font-mono" style={{ width: `${pctP}%` }} title={`${d.partiel} en cours`}>{d.partiel > 0 && `${d.partiel}`}</div>}
                            {pctN > 0 && <div className="bg-red-500 flex items-center justify-center text-white text-[10px] font-mono" style={{ width: `${pctN}%` }} title={`${d.non_acquis} non acquis`}>{d.non_acquis > 0 && `${d.non_acquis}`}</div>}
                            {pctNE > 0 && <div className="bg-rule flex items-center justify-center text-muted2 text-[10px] font-mono" style={{ width: `${pctNE}%` }} title={`${d.non_evalue} non évalués`}>{d.non_evalue > 0 && `${d.non_evalue}`}</div>}
                        </div>
                    </div>
                );
            })}
            <div className="flex flex-wrap gap-3 text-[11px] font-mono text-muted2 mt-2 pt-2 border-t border-rule">
                <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-emerald-500 inline-block"></span> Acquis</span>
                <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-amber-500 inline-block"></span> En cours</span>
                <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-red-500 inline-block"></span> Non acquis</span>
                <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-rule inline-block"></span> Non évalué</span>
            </div>
        </div>
    );
}

export default function BilanGlobal() {
    const { id } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [schoolYear, setSchoolYear] = useState("all");

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const params = schoolYear !== "all" ? `?school_year=${encodeURIComponent(schoolYear)}` : "";
                const res = await api.get(`/alternants/${id}/bilan-global${params}`);
                setData(res.data);
            } catch (err) {
                toast.error(err.response?.data?.detail || "Impossible de charger le bilan global");
            } finally {
                setLoading(false);
            }
        })();
    }, [id, schoolYear]);

    const yearOptions = useMemo(() => {
        // Build a small list of the current + previous school year for filtering
        const now = new Date();
        const y = now.getFullYear();
        const cur = now.getMonth() >= 8 ? `${y}-${y + 1}` : `${y - 1}-${y}`;
        const prev = now.getMonth() >= 8 ? `${y - 1}-${y}` : `${y - 2}-${y - 1}`;
        return [cur, prev];
    }, []);

    const evaluated = useMemo(() => (data?.competencies || []).filter((c) => c.level !== "non_evalue"), [data]);

    const exportPdf = () => {
        if (!data) return;
        const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
        const a = data.alternant;
        const today = new Date().toLocaleDateString("fr-FR");
        const domainsHtml = data.domains.map((d) => {
            const total = d.total || 1;
            const barW = (n) => `${(n / total) * 100}%`;
            return `
                <div style="margin-bottom:14px">
                    <div style="display:flex;justify-content:space-between;font-size:12px;margin-bottom:4px"><strong>${esc(d.label)}</strong><span>${d.acquis + d.partiel}/${d.total} maîtrisées</span></div>
                    <div style="display:flex;height:18px;border:1px solid #d1d5db">
                        ${d.acquis ? `<div style="background:#10B981;width:${barW(d.acquis)};color:#fff;font-size:10px;text-align:center;line-height:18px">${d.acquis}</div>` : ""}
                        ${d.partiel ? `<div style="background:#F59E0B;width:${barW(d.partiel)};color:#fff;font-size:10px;text-align:center;line-height:18px">${d.partiel}</div>` : ""}
                        ${d.non_acquis ? `<div style="background:#DC2626;width:${barW(d.non_acquis)};color:#fff;font-size:10px;text-align:center;line-height:18px">${d.non_acquis}</div>` : ""}
                        ${d.non_evalue ? `<div style="background:#e5e7eb;width:${barW(d.non_evalue)};color:#6B7280;font-size:10px;text-align:center;line-height:18px">${d.non_evalue}</div>` : ""}
                    </div>
                </div>
            `;
        }).join("");
        const rowsHtml = (data.competencies || []).map((c) => {
            const meta = LEVEL_META[c.level] || LEVEL_META.non_evalue;
            const evo = EVOLUTION_META[c.evolution] || EVOLUTION_META.stable;
            return `
                <tr>
                  <td style="text-transform:uppercase;font-family:monospace;font-size:10px">${esc(c.code)}</td>
                  <td>${esc(c.label)}</td>
                  <td style="text-align:center"><span style="background:${meta.chip};color:#fff;padding:2px 8px;font-size:10px;font-weight:bold">${esc(meta.label)}</span></td>
                  <td style="text-align:center;font-size:11px">${esc(evo.label)}</td>
                  <td style="text-align:right;font-family:monospace">${c.count}</td>
                </tr>
            `;
        }).join("");
        const period = data.period.first_evaluation
            ? `${data.period.first_evaluation} → ${data.period.last_evaluation}`
            : "Aucune évaluation enregistrée";

        const html = `<!doctype html><html lang="fr"><head><meta charset="utf-8" />
<title>Bilan global · ${esc(a.prenom)} ${esc(a.nom)}</title>
<style>
  @page { size: A4; margin: 1.5cm; }
  body { font-family: -apple-system, "Segoe UI", Arial, sans-serif; color: #0a0a0a; }
  header { border-bottom: 2px solid #0a0a0a; padding-bottom: 12px; margin-bottom: 18px; }
  h1 { margin: 0; font-size: 24px; }
  h2 { font-size: 15px; margin: 22px 0 10px; border-left: 4px solid #15A36A; padding-left: 8px; }
  .meta { font-size: 11px; color: #6B7280; letter-spacing: .12em; text-transform: uppercase; }
  .kpi-row { display: flex; gap: 12px; margin: 16px 0; }
  .kpi { flex: 1; border: 1px solid #d1d5db; padding: 10px; }
  .kpi-label { font-size: 10px; color: #6B7280; text-transform: uppercase; letter-spacing: .1em; }
  .kpi-value { font-size: 22px; font-weight: bold; margin-top: 2px; }
  table { width: 100%; border-collapse: collapse; font-size: 11px; }
  th, td { border: 1px solid #d1d5db; padding: 5px 8px; vertical-align: top; }
  th { background: #f3f4f6; text-align: left; font-size: 10px; text-transform: uppercase; letter-spacing: .05em; }
  .print-btn { position: fixed; top: 12px; right: 12px; padding: 10px 18px; background: #15A36A; color: #fff; border: none; cursor: pointer; font-weight: bold; }
  @media print { .print-btn { display: none; } }
</style>
</head><body>
<button class="print-btn" onclick="window.print()">Imprimer / PDF</button>
<header>
  <div class="meta">Profawz · Bilan global</div>
  <h1>${esc(a.prenom)} ${esc(a.nom)}</h1>
  <div style="font-size:12px;color:#6B7280;margin-top:2px">${esc(a.referentiel_label || "")} · ${esc(a.nom_etablissement || "")}<br />Période : ${esc(period)} · Édité le ${today}</div>
</header>

<div class="kpi-row">
  <div class="kpi"><div class="kpi-label">Acquis</div><div class="kpi-value" style="color:#15A36A">${data.stats.acquis}</div></div>
  <div class="kpi"><div class="kpi-label">En cours</div><div class="kpi-value" style="color:#F59E0B">${data.stats.partiel}</div></div>
  <div class="kpi"><div class="kpi-label">Non acquis</div><div class="kpi-value" style="color:#DC2626">${data.stats.non_acquis}</div></div>
  <div class="kpi"><div class="kpi-label">Non évalué</div><div class="kpi-value" style="color:#6B7280">${data.stats.non_evalue}</div></div>
  <div class="kpi"><div class="kpi-label">Évaluations</div><div class="kpi-value">${data.total_evaluations}</div></div>
</div>

<h2>Profil par domaine</h2>
${domainsHtml}

<h2>Détail par compétence</h2>
<table>
  <thead><tr><th>Code</th><th>Compétence</th><th style="text-align:center">Niveau global</th><th style="text-align:center">Évolution</th><th style="text-align:right">Évaluations</th></tr></thead>
  <tbody>${rowsHtml}</tbody>
</table>

<p style="font-size:10px;color:#6B7280;margin-top:18px">
  Méthode : le niveau global est calculé par pondération temporelle — les évaluations les plus récentes comptent davantage que les plus anciennes. Il ne s'agit pas d'une simple moyenne.
</p>
</body></html>`;
        const blob = new Blob([html], { type: "text/html;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const w = window.open(url, "_blank", "noopener,noreferrer");
        if (!w) {
            URL.revokeObjectURL(url);
            toast.error("Autorisez les pop-ups pour l'export PDF.");
            return;
        }
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-paper">
                <Navbar />
                <main className="max-w-6xl mx-auto px-4 py-16 text-center text-muted2">
                    <Loader2 size={24} className="animate-spin inline-block mr-2 text-orange" /> Génération du bilan global…
                </main>
                <Footer />
            </div>
        );
    }
    if (!data) return null;

    const a = data.alternant;
    const period = data.period.first_evaluation
        ? `${data.period.first_evaluation} → ${data.period.last_evaluation}`
        : "Aucune évaluation enregistrée pour cette période";

    return (
        <div className="min-h-screen bg-paper" data-testid="bilan-global-page">
            <Navbar />
            <main className="max-w-6xl mx-auto px-4 py-8">
                <Link to={`/progression/${id}`} className="text-xs font-mono text-muted2 hover:text-orange inline-flex items-center gap-1 mb-3">
                    <ArrowLeft size={12} /> Progression de {a.prenom}
                </Link>
                <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
                    <div>
                        <div className="label-eyebrow flex items-center gap-1"><Award size={12} /> Bilan global</div>
                        <h1 className="h-display text-3xl sm:text-4xl font-semibold" data-testid="bilan-global-title">
                            {a.prenom} {a.nom}
                        </h1>
                        <p className="text-sm text-muted2 mt-1 inline-flex items-center gap-2">
                            <GraduationCap size={13} /> {a.referentiel_label} · {a.niveau === "2eme" ? "2ème année" : "1ère année"} · <span className="font-mono text-xs">{period}</span>
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-2 self-start sm:self-auto">
                        <select value={schoolYear} onChange={(e) => setSchoolYear(e.target.value)} className="field-input !py-2 !text-xs" data-testid="bilan-global-year-filter">
                            <option value="all">Toutes les années</option>
                            {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
                        </select>
                        <button onClick={exportPdf} className="btn-primary !text-xs !px-4 !py-2 inline-flex" data-testid="bilan-global-export-pdf">
                            <FileDown size={14} /> Exporter en PDF
                        </button>
                    </div>
                </div>

                {/* KPI CARDS */}
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
                    {["acquis", "partiel", "non_acquis", "non_evalue"].map((k) => {
                        const m = LEVEL_META[k];
                        return (
                            <div key={k} className={`panel p-4 ${m.bg} ${m.border}`}>
                                <div className="label-eyebrow flex items-center gap-1"><m.icon size={12} /> {m.label}</div>
                                <div className={`h-display text-3xl font-bold mt-1 ${m.color}`} data-testid={`bilan-global-stat-${k}`}>{data.stats[k] || 0}</div>
                            </div>
                        );
                    })}
                    <div className="panel p-4">
                        <div className="label-eyebrow flex items-center gap-1"><BarChart3 size={12} /> Évaluations</div>
                        <div className="h-display text-3xl font-bold mt-1" data-testid="bilan-global-stat-total">{data.total_evaluations}</div>
                    </div>
                </div>

                {/* CHART */}
                <div className="panel p-6 mb-6">
                    <div className="label-eyebrow mb-4">Profil par domaine</div>
                    <BarChart domains={data.domains} />
                </div>

                {/* TABLE */}
                <div className="panel overflow-x-auto" data-testid="bilan-global-table-wrapper">
                    <table className="w-full text-sm" data-testid="bilan-global-table">
                        <thead className="bg-ink text-white">
                            <tr>
                                <th className="text-left px-3 py-2 text-[11px] font-mono tracking-wider">Code</th>
                                <th className="text-left px-3 py-2 text-[11px] font-mono tracking-wider">Compétence</th>
                                <th className="text-center px-3 py-2 text-[11px] font-mono tracking-wider">Niveau global</th>
                                <th className="text-center px-3 py-2 text-[11px] font-mono tracking-wider">Évolution</th>
                                <th className="text-right px-3 py-2 text-[11px] font-mono tracking-wider">Évaluations</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(data.competencies || []).map((c) => {
                                const m = LEVEL_META[c.level] || LEVEL_META.non_evalue;
                                const evo = EVOLUTION_META[c.evolution] || EVOLUTION_META.stable;
                                const Evo = evo.icon;
                                return (
                                    <tr key={c.code} className="border-t border-rule hover:bg-orange/5" data-testid={`bilan-global-row-${c.code}`}>
                                        <td className="px-3 py-2 font-mono text-xs">{c.code}</td>
                                        <td className="px-3 py-2 text-xs">{c.label}</td>
                                        <td className="px-3 py-2 text-center">
                                            <span className={`pill ${m.bg} ${m.color} ${m.border} border text-[10px]`}>{m.label}</span>
                                        </td>
                                        <td className={`px-3 py-2 text-center text-xs ${evo.color}`}>
                                            <span className="inline-flex items-center gap-1"><Evo size={11} /> {evo.label}</span>
                                        </td>
                                        <td className="px-3 py-2 text-right font-mono text-xs">{c.count}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                <p className="text-[11px] text-muted2 mt-3 italic max-w-3xl">
                    Le niveau global n&apos;est <strong>pas une simple moyenne</strong> : chaque évaluation est pondérée selon sa date (les plus récentes comptent davantage). Statuts « non évalué » exclus du calcul. Total évalué&nbsp;: <strong>{evaluated.length}</strong>&nbsp;/&nbsp;{data.competencies.length} compétences du référentiel.
                </p>
            </main>
            <Footer />
        </div>
    );
}
